import { useEffect } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import { useRouter } from 'next/router';
import Script from 'next/script';

import type { RecommendationContext } from '@/lib/useDynamicYield';

interface ConsentEventPayload {
  hasConsentedBehavioural: boolean;
}

const DynamicYieldScripts = ({
  context = {},
}: {
  context: RecommendationContext;
}) => {
  const router = useRouter();

  const isProduction = process.env.NEXT_PUBLIC_SANITY_DATASET?.includes('prod');
  let sectionId = isProduction ? 9880147 : 9880019;

  if (process.env.NODE_ENV === 'development') {
    sectionId = 9880229;
  }

  let consent_given = useLocalStorageValue('userActiveConsent').value;
  consent_given = consent_given === null ? false : consent_given;

  useEffect(() => {
    if (window?.DY?.recommendationContext) {
      window.DY.recommendationContext = context;
    }
  }, [context]);

  useEffect(() => {
    const handleConsentChange = (event: CustomEvent<ConsentEventPayload>) => {
      const { hasConsentedBehavioural } = event.detail;
      window.updateConsentStatus(hasConsentedBehavioural);
      window.localStorage.setItem(
        'userActiveConsent',
        `${hasConsentedBehavioural}`,
      );
    };

    const handleConsentClosed = () => {
      router.reload();
    };

    window.addEventListener('cookieConsentChange', handleConsentChange);
    window.addEventListener('cookieConsentClosed', handleConsentClosed);

    return () => {
      window.removeEventListener('cookieConsentChange', handleConsentChange);
      window.removeEventListener('cookieConsentClosed', handleConsentClosed);
    };
  });

  return (
    <>
      <Script
        id="dy-recommendation-context"
        type="text/javascript"
        key="dy-context-script"
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: `
            window.DY = window.DY || {};
            window.DYO = window.DYO || {};
            DY.recommendationContext = ${JSON.stringify(context)};
            DY.userActiveConsent = { accepted: ${consent_given} };
            function updateConsentStatus(accepted) {
              DY.userActiveConsent = { accepted };
              DYO?.ActiveConsent?.updateConsentAcceptedStatus(accepted);
            };
          `,
        }}
      />
      <Script
        id="dy-api-dynamic-script"
        type="text/javascript"
        key="dy-api-dynamic-script"
        defer
        src={`//cdn-eu.dynamicyield.com/api/${sectionId}/api_dynamic.js`}
      />
      <Script
        id="dy-api-static-script"
        type="text/javascript"
        key="dy-api-static-script"
        defer
        src={`//cdn-eu.dynamicyield.com/api/${sectionId}/api_static.js`}
      />
    </>
  );
};

export { DynamicYieldScripts };
